import type { ReactElement, ReactNode } from 'react'
import { Link } from 'react-router-dom'

import { cn } from '../../lib/cn'
import { logoFor, useBranding } from '../../lib/branding'
import { useT, useTNode } from '../../lib/i18n'
import { LocaleSwitcher } from '../../components/ui/LocaleSwitcher'

export interface AuthLayoutProps {
  title: string
  /** One line telling the visitor what this screen does for them. */
  subtitle?: ReactNode
  children: ReactNode
  /** Sign-up prompt, reset link — sits under the card. */
  footer?: ReactNode
}

/**
 * The themed frame for every unauthenticated screen. A tenant's chrome colour paints the
 * left panel, its logo replaces the wordmark, and its login background image sits behind —
 * all three come from `GET /whitelabel/resolve`, applied before this ever renders.
 *
 * The language switcher lives here rather than on each card: docs/I18N.md puts it on the
 * sign-in card, and every unauthenticated screen is a sign-in card of some kind. Nobody has a
 * `User.locale` yet at this point, so this is the last place the choice can be made before the
 * password reset mail goes out in the wrong language.
 */
export const AuthLayout = ({ title, subtitle, children, footer }: AuthLayoutProps): ReactElement => {
  const { branding } = useBranding()
  const t = useT()
  const tn = useTNode()
  const chromeLogo = logoFor(branding, 'chrome')
  const paperLogo = logoFor(branding, 'paper')

  return (
    <div className="flex min-h-dvh bg-paper">
      {/* Brand panel — chrome, with the tenant's login background if there is one */}
      <aside
        className="relative hidden w-[42%] max-w-xl flex-col justify-between overflow-hidden bg-chrome-900 p-10 lg:flex"
        aria-hidden="true"
      >
        {branding.loginBackgroundUrl ? (
          <img
            src={branding.loginBackgroundUrl}
            alt=""
            className="absolute inset-0 h-full w-full object-cover opacity-35"
          />
        ) : null}

        <div className="relative">
          {chromeLogo ? (
            <img src={chromeLogo} alt="" className="max-h-9 max-w-44 object-contain" />
          ) : (
            <span className="t-h2 text-chrome-ink">{branding.productName}</span>
          )}
        </div>

        <div className="relative flex flex-col gap-3">
          <p className="t-h1 text-chrome-ink">{t('auth.layout.headline')}</p>
          <p className="t-body max-w-sm text-chrome-mute">{t('auth.layout.body')}</p>
          {/* The one flourish that belongs here: a still Now Rail. */}
          <div className="mt-4 flex h-7 items-center gap-px rounded-sm bg-chrome-800 px-2">
            {Array.from({ length: 24 }, (_, hour) => (
              <span
                key={hour}
                className="h-3 flex-1 rounded-[1px]"
                style={{
                  backgroundColor: 'var(--brass)',
                  opacity: 0.06 + Math.max(0, Math.sin(((hour - 5) / 24) * Math.PI * 2)) * 0.5,
                }}
              />
            ))}
          </div>
        </div>
      </aside>

      {/* Form column */}
      <div className="flex min-w-0 flex-1 items-center justify-center px-4 py-10">
        <div className="w-full max-w-sm">
          <div className="mb-8 flex items-center gap-3 lg:hidden">
            {paperLogo ? (
              <img src={paperLogo} alt="" className="max-h-8 max-w-36 object-contain" />
            ) : (
              <span className="t-h2 text-ink">{branding.productName}</span>
            )}
          </div>

          <h1 className="t-h1 text-ink">{title}</h1>
          {subtitle ? <p className="t-body mt-2 text-ink-mute">{subtitle}</p> : null}

          <div className="mt-6">{children}</div>

          {footer ? <div className="mt-6 border-t border-hair pt-5">{footer}</div> : null}

          <div className="mt-10 flex flex-wrap items-center justify-between gap-3">
            <p className={cn('t-mono text-ink-mute')}>
              {branding.hidePoweredBy ? (
                <Link to="/" className="ring-focus hover:text-ink-mute">
                  {branding.productName}
                </Link>
              ) : (
                tn('auth.layout.poweredBy', {
                  product: (chunk) => (
                    <Link to="/" className="ring-focus hover:text-ink-mute">
                      {chunk}
                    </Link>
                  ),
                })
              )}
              {branding.supportEmail ? (
                <>
                  {' · '}
                  <a
                    href={`mailto:${branding.supportEmail}`}
                    className="ring-focus hover:text-ink-mute"
                  >
                    {branding.supportEmail}
                  </a>
                </>
              ) : null}
            </p>

            <LocaleSwitcher variant="inline" />
          </div>
        </div>
      </div>
    </div>
  )
}
